import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import syncService from "../../services/syncService";

/** Normalize API status so UI always sees camelCase + numbers */
export const normalizeSyncStatus = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    online: raw.online ?? raw.connection?.online,
    pendingLocalChanges: Number(
      raw.pendingLocalChanges ??
        raw.pending_local_changes ??
        raw.pendingChanges ??
        0
    ),
    lastPushAt: raw.lastPushAt ?? raw.last_push_at ?? null,
    lastPullAt: raw.lastPullAt ?? raw.last_pull_at ?? null,
    lastError: raw.lastError ?? raw.last_error ?? null,
    connection: raw.connection,
  };
};

const normalizeSyncRun = (r) => {
  if (!r || typeof r !== "object") return null;
  const details = Array.isArray(r.details) ? r.details.map((d) => ({
    table: d.table,
    applied: Number(d.applied ?? 0),
    conflicted: Number(d.conflicted ?? 0),
  })) : [];
  return {
    id: r.id,
    startedAt: r.startedAt ?? r.started_at ?? null,
    finishedAt: r.finishedAt ?? r.finished_at ?? null,
    direction: r.direction || "both",
    itemsPushed: Number(r.itemsPushed ?? r.items_pushed ?? 0),
    itemsPulled: Number(r.itemsPulled ?? r.items_pulled ?? 0),
    status: r.status || "unknown",
    message: r.message || "",
    details,
  };
};

const normalizeConflictRow = (c) => {
  if (!c || typeof c !== "object") return null;
  return {
    id: c.id,
    entityType: c.entityType ?? c.entity_type ?? c.table,
    externalId: c.externalId ?? c.external_id ?? c.recordId,
    summary: c.summary ?? c.message ?? "",
    createdAt: c.createdAt ?? c.created_at ?? c.detectedAt,
    resolution: c.resolution,
  };
};

export const fetchSyncStatus = createAsyncThunk(
  "sync/fetchStatus",
  async (_, { rejectWithValue }) => {
    try {
      const res = await syncService.getStatus();
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to load sync status"
      );
    }
  }
);

export const fetchSyncRuns = createAsyncThunk(
  "sync/fetchRuns",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await syncService.getRuns(params);
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to load sync history"
      );
    }
  }
);

export const triggerSyncNow = createAsyncThunk(
  "sync/triggerNow",
  async (_, { rejectWithValue }) => {
    try {
      const res = await syncService.triggerSync();
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to trigger sync"
      );
    }
  }
);

export const testSyncConnection = createAsyncThunk(
  "sync/testConnection",
  async (_, { rejectWithValue }) => {
    try {
      const res = await syncService.testConnection();
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to test connection"
      );
    }
  }
);

export const fetchSyncConflicts = createAsyncThunk(
  "sync/fetchConflicts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await syncService.getConflicts(params);
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to load sync conflicts"
      );
    }
  }
);

export const resolveSyncConflict = createAsyncThunk(
  "sync/resolveConflict",
  async ({ id, resolution }, { rejectWithValue }) => {
    try {
      const res = await syncService.resolveConflict(id, { resolution });
      return res.data?.data ?? { id, resolution };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to resolve conflict"
      );
    }
  }
);

const syncSlice = createSlice({
  name: "sync",
  initialState: {
    status: null,
    runs: [],
    conflicts: [],
    loadingStatus: false,
    loadingRuns: false,
    loadingConflicts: false,
    triggeringSync: false,
    testingConnection: false,
    resolvingConflictId: null,
    lastManualSync: null,
    error: null,
  },
  reducers: {
    clearSyncError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSyncStatus.pending, (state) => {
        state.loadingStatus = true;
        state.error = null;
      })
      .addCase(fetchSyncStatus.fulfilled, (state, action) => {
        state.loadingStatus = false;
        state.status = normalizeSyncStatus(action.payload);
      })
      .addCase(fetchSyncStatus.rejected, (state, action) => {
        state.loadingStatus = false;
        state.error = action.payload;
      })

      .addCase(fetchSyncRuns.pending, (state) => {
        state.loadingRuns = true;
        state.error = null;
      })
      .addCase(fetchSyncRuns.fulfilled, (state, action) => {
        state.loadingRuns = false;
        const list = Array.isArray(action.payload) ? action.payload : [];
        state.runs = list.map(normalizeSyncRun).filter(Boolean);
      })
      .addCase(fetchSyncRuns.rejected, (state, action) => {
        state.loadingRuns = false;
        state.error = action.payload;
      })

      .addCase(triggerSyncNow.pending, (state) => {
        state.triggeringSync = true;
        state.error = null;
      })
      .addCase(triggerSyncNow.fulfilled, (state, action) => {
        state.triggeringSync = false;
        const p = action.payload;
        if (p && typeof p === "object") {
          const pushed = Number(
            p.rows_applied ?? p.rowsApplied ?? p.itemsPushed ?? p.items_pushed ?? 0
          );
          state.lastManualSync = {
            at: new Date().toISOString(),
            itemsPushed: pushed,
            itemsPulled: Number(p.itemsPulled ?? p.items_pulled ?? 0),
            status: p.status,
            online: p.online,
            note: p.note,
          };
          if (typeof p.online === "boolean" && state.status) {
            state.status.online = p.online;
          }
        }
      })
      .addCase(triggerSyncNow.rejected, (state, action) => {
        state.triggeringSync = false;
        state.error = action.payload;
      })

      .addCase(testSyncConnection.pending, (state) => {
        state.testingConnection = true;
        state.error = null;
      })
      .addCase(testSyncConnection.fulfilled, (state, action) => {
        state.testingConnection = false;
        const base = normalizeSyncStatus(state.status) || {};
        const conn = action.payload?.connection || base.connection || {};
        state.status = {
          ...base,
          // Trust the latest connection test for the online flag so the
          // Sync Center header reflects "Test host" immediately, without
          // waiting for another status fetch.
          online:
            typeof conn.online === "boolean"
              ? conn.online
              : base.online,
          connection: conn,
          lastTestedAt: new Date().toISOString(),
        };
      })
      .addCase(testSyncConnection.rejected, (state, action) => {
        state.testingConnection = false;
        state.error = action.payload;
      })

      .addCase(fetchSyncConflicts.pending, (state) => {
        state.loadingConflicts = true;
        state.error = null;
      })
      .addCase(fetchSyncConflicts.fulfilled, (state, action) => {
        state.loadingConflicts = false;
        const list = Array.isArray(action.payload) ? action.payload : [];
        state.conflicts = list.map(normalizeConflictRow).filter(Boolean);
      })
      .addCase(fetchSyncConflicts.rejected, (state, action) => {
        state.loadingConflicts = false;
        state.error = action.payload;
      })

      .addCase(resolveSyncConflict.pending, (state, action) => {
        state.error = null;
        state.resolvingConflictId = action.meta?.arg?.id ?? null;
      })
      .addCase(resolveSyncConflict.fulfilled, (state, action) => {
        state.resolvingConflictId = null;
        const resolvedId = action.payload?.id;
        if (resolvedId != null) {
          state.conflicts = state.conflicts.filter(
            (c) => String(c.id) !== String(resolvedId)
          );
        }
      })
      .addCase(resolveSyncConflict.rejected, (state, action) => {
        state.resolvingConflictId = null;
        state.error = action.payload;
      });
  },
});

export const { clearSyncError } = syncSlice.actions;
export default syncSlice.reducer;
